/**
 * Player names in online play: shown to the opponent and spectators, and on the leaderboard.
 * Shared by the browser, which says at once what is wrong with a name, and the rooms server,
 * which decides (src/online/room.ts hello), so a modified browser gains nothing.
 *
 * - What a name is written with (cleanName): letters, combining marks (two at most on a letter,
 *   so no "Zalgo" stacks), digits, spaces and . _ ' -, after NFKC (fullwidth and other
 *   compatibility forms become plain ones); 24 characters at most. Letters that show nothing
 *   (Hangul fillers) are dropped, so no name looks blank.
 * - Which names are not allowed (nameProblem): letters of more than one alphabet (Latin mixed
 *   with look-alike Cyrillic letters, say); the game's own names and names that pass for its
 *   staff; and offensive names, found by the obscenity library's English word list however they
 *   are spelled out (capitals, s p a c e s, digits for letters, look-alike letters, a letter held
 *   longer). Words in other languages are blocked by the site's operator (blockedBy, the D1 table
 *   name_blocks, scripts/moderate-names.ts).
 * - When two names are the same name (nameKey): case, accents, separators and look-alike letters
 *   set aside, so that nobody takes a look-alike of another player's name (the rooms server
 *   checks the leaderboard's names).
 */
import { DataSet, RegExpMatcher, englishDataset, englishRecommendedTransformers } from 'obscenity';

export const NAME_MAX = 24;

/** Why a name cannot be used. */
export type NameProblem = 'scripts' | 'reserved' | 'offensive';

const SEPARATORS = /[ ._'-]/g;

/** Display names: the name as it is shown, or null when nothing usable is left. */
export function cleanName(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const cleaned = raw
    .normalize('NFKC')
    // Hangul fillers: letters that show nothing.
    .replace(/[ᅟᅠㅤﾠ]/g, '')
    .replace(/[^\p{L}\p{M}\p{Nd} ._'-]/gu, '')
    // A mark belongs on a letter, and two at most do.
    .replace(/(?<![\p{L}\p{M}])\p{M}+/gu, '')
    .replace(/(\p{M}{2})\p{M}+/gu, '$1')
    // One separator at a time, and none at the ends.
    .replace(/([ ._'-])[ ._'-]+/g, '$1')
    .replace(/^[ ._'-]+|[ ._'-]+$/g, '');
  const name = [...cleaned]
    .slice(0, NAME_MAX)
    .join('')
    .replace(/[ ._'-]+$/, '');
  return /[\p{L}\p{Nd}]/u.test(name) ? name : null;
}

/** Why a (clean) name cannot be used, or null. */
export function nameProblem(name: string): NameProblem | null {
  if (!oneAlphabet(name)) return 'scripts';
  if (reserved(name)) return 'reserved';
  if (offensive(name)) return 'offensive';
  return null;
}

/** A player's name fit to use: clean and without a problem, or null. */
export function usableName(raw: unknown): string | null {
  const name = cleanName(raw);
  return name && !nameProblem(name) ? name : null;
}

/**
 * A label fit to show that claims no one's identity (the sides of a shared replay, where a duel's
 * "You" and "The Clerk" are what they were called): clean and not offensive, or null.
 */
export function inoffensiveName(raw: unknown): string | null {
  const name = cleanName(raw);
  return name && !offensive(name) ? name : null;
}

/**
 * What a name looks like, for telling names apart: names with the same key read alike (case,
 * accents, spacing and look-alike letters set aside: "B0b", "b.o.b", "Вов" and "Bob").
 */
export function nameKey(name: string): string {
  let folded = '';
  for (const c of name.normalize('NFKC').toLowerCase().normalize('NFD').replace(/\p{M}/gu, ''))
    folded += LOOKALIKES[c] ?? c;
  return folded.replace(SEPARATORS, '').replace(/rn/g, 'm').replace(/vv/g, 'w');
}

/** Whether a name contains one of the operator's blocked words (compared as keys). */
export function blockedBy(name: string, blocked: readonly string[]): boolean {
  const key = nameKey(name);
  return blocked.some((word) => {
    const w = nameKey(word);
    return w.length > 0 && key.includes(w);
  });
}

// ---- Alphabets --------------------------------------------------------------------------------

const SCRIPTS = [
  'Latin',
  'Greek',
  'Cyrillic',
  'Armenian',
  'Georgian',
  'Hebrew',
  'Arabic',
  'Syriac',
  'Thaana',
  'Devanagari',
  'Bengali',
  'Gurmukhi',
  'Gujarati',
  'Oriya',
  'Tamil',
  'Telugu',
  'Kannada',
  'Malayalam',
  'Sinhala',
  'Thai',
  'Lao',
  'Tibetan',
  'Myanmar',
  'Khmer',
  'Mongolian',
  'Ethiopic',
  'Hangul',
  'Hiragana',
  'Katakana',
  'Bopomofo',
  'Han',
] as const;
type Script = (typeof SCRIPTS)[number];

const inScript = Object.fromEntries(
  SCRIPTS.map((s) => [s, new RegExp(`\\p{Script_Extensions=${s}}`, 'u')]),
) as Record<Script, RegExp>;

/** The alphabets a name may use together: one, or those written together in CJK languages. */
const PROFILES: readonly (readonly Script[])[] = [
  ...SCRIPTS.map((s) => [s]),
  ['Han', 'Hiragana', 'Katakana'],
  ['Han', 'Hangul'],
  ['Han', 'Bopomofo'],
];

/** Letters of no alphabet in particular (modifier letters such as the ʻokina). */
const NEUTRAL = /[\p{Script=Common}\p{Script=Inherited}]/u;

function oneAlphabet(name: string): boolean {
  const letters = [...name].filter((c) => /\p{L}/u.test(c) && !NEUTRAL.test(c));
  return PROFILES.some((profile) => letters.every((c) => profile.some((s) => inScript[s].test(c))));
}

/**
 * Letters that read as other letters: look-alikes from other alphabets (lower case, so that
 * capitals that look alike count too), digits used as letters, and i for l (I and l are one
 * stroke in many fonts).
 */
const LOOKALIKES: Record<string, string> = {
  а: 'a',
  в: 'b',
  г: 'r',
  е: 'e',
  ё: 'e',
  к: 'k',
  м: 'm',
  н: 'h',
  о: 'o',
  п: 'n',
  р: 'p',
  с: 'c',
  т: 't',
  у: 'y',
  х: 'x',
  ь: 'b',
  ѕ: 's',
  і: 'l',
  ј: 'j',
  ԁ: 'd',
  ӏ: 'l',
  ԛ: 'q',
  ԝ: 'w',
  α: 'a',
  β: 'b',
  ε: 'e',
  ζ: 'z',
  η: 'n',
  ι: 'l',
  κ: 'k',
  μ: 'm',
  ν: 'v',
  ο: 'o',
  ρ: 'p',
  τ: 't',
  υ: 'u',
  χ: 'x',
  ı: 'l',
  ɡ: 'g',
  i: 'l',
  '0': 'o',
  '1': 'l',
  '3': 'e',
  '4': 'a',
  '5': 's',
  '7': 't',
  '8': 'b',
  '9': 'g',
};

// ---- Reserved names ---------------------------------------------------------------------------

/** The game's and its computer players' names. */
const RESERVED_NAMES = [
  'you',
  'south',
  'north',
  'computer',
  'the apprentice',
  'the clerk',
  'the appraiser',
  'old tallow',
  'the magpie',
  'the widow',
  'the broker',
].map(nameKey);
/** Words a name may not have, as they pass for the game itself or its staff… */
const RESERVED_WORDS = [
  'pawned',
  'admin',
  'administrator',
  'moderator',
  'mod',
  'mods',
  'official',
  'staff',
  'support',
  'system',
  'server',
  'dev',
  'developer',
  'owner',
  'gm',
  'sysop',
].map(nameKey);
/** …and the longer of them may not start one either ("PAWNEDteam", "admin42"). */
const RESERVED_STARTS = ['pawned', 'admin', 'moderator', 'official', 'developer'].map(nameKey);

/** The words of a name: split at separators and where a lower-case letter meets a capital. */
function words(name: string): string[] {
  return name
    .normalize('NFKC')
    .replace(/(\p{Ll})(\p{Lu})/gu, '$1 $2')
    .split(SEPARATORS)
    .filter(Boolean);
}

function reserved(name: string): boolean {
  const key = nameKey(name);
  if (RESERVED_NAMES.includes(key)) return true;
  if (RESERVED_STARTS.some((r) => key.startsWith(r))) return true;
  return words(name).some((w) => RESERVED_WORDS.includes(nameKey(w)));
}

// ---- Offensive names --------------------------------------------------------------------------

/**
 * Real names and places that contain a listed word (Japanese, Thai, Indian, Russian names and
 * English surnames among them): they are names, not insults.
 */
const NAMES_NOT_INSULTS = [
  'fuku',
  'anush',
  'anusree',
  'anuska',
  'analia',
  'analis',
  'arsen',
  'semenov',
  'semenya',
  'dickson',
  'dickerson',
  'dickinson',
  'cumming',
  'cummins',
  'peniston',
  'shiitake',
  'shitara',
  'shittu',
  'shitij',
  'wankel',
  'wankhede',
  'pornchai',
  'pornthip',
  'porntip',
  'pornpimol',
  'pornpan',
  'pornsak',
  'pissarro',
  'slutsk',
  'bich ngoc',
];

/** The matcher, built on first use: compiling the list takes a moment, which app start skips. */
let built: RegExpMatcher | null = null;
const matcher = (): RegExpMatcher =>
  (built ??= new RegExpMatcher({
    ...new DataSet<{ originalWord: string }>()
      .addAll(englishDataset)
      .addPhrase((p) => NAMES_NOT_INSULTS.reduce((b, n) => b.addWhitelistedTerm(n), p))
      .build(),
    ...englishRecommendedTransformers,
  }));

/** "w o r d", "w.o.r.d": a word spelled out letter by letter, read as the word. */
const spelledOut = (text: string): string =>
  text.replace(
    /(?<![\p{L}\p{N}])[\p{L}\p{N}](?:[ ._'-][\p{L}\p{N}](?![\p{L}\p{N}])){2,}/gu,
    (run) => run.replace(SEPARATORS, ''),
  );

/**
 * Whether a name is offensive, read in every way it could be meant: without accents (so that
 * "Arsène" is the name "Arsene"), with its words apart ("BigWord" as "Big Word": some words count
 * only whole), with letters spelled out one by one read together, and with a letter held for
 * three or more read once.
 */
function offensive(name: string): boolean {
  const bare = name.normalize('NFD').replace(/\p{M}/gu, '');
  const forms = new Set([bare, words(bare).join(' ')]);
  for (const form of [...forms]) forms.add(spelledOut(form));
  for (const form of [...forms]) forms.add(form.replace(/(\p{L})\1{2,}/gu, '$1'));
  return [...forms].some((form) => matcher().hasMatch(form));
}
